import {RawAxiosRequestConfig} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {AuthApi} from "components/src/api/portal/generated";
import {AuthenticatedRoute} from "components/src/auth/AuthenticatedRoute";
import {PortalForgotPassword} from "components/src/auth/PortalForgotPassword";
import {PortalLogin} from "components/src/auth/PortalLogin";
import {PortalPasswordReset} from "components/src/auth/PortalPasswordReset";
import {PortalSignup} from "components/src/auth/PortalSignup";
import {Success} from "components/src/auth/Success";
import {Tfa} from "components/src/auth/Tfa";
import {ErrorPage} from "components/src/common/ErrorPage";
import {Loading} from "components/src/common/Loading";
import {AuthenticatedUserContextProvider} from "components/src/context/AuthenticatedUserContextProvider";
import {SpinnerStatusContextProvider} from "components/src/context/SpinnerStatusContextProvider";
import {AppLayout} from "components/src/layout/AppLayout";
import {LoginLayout} from "components/src/layout/LoginLayout";
import {PDFGeneratePassword} from "components/src/portal/PDFGeneratePassword";
import {PDFReply} from "components/src/portal/PDFReply";
import {PortalHome} from "components/src/portal/PortalHome";
import React, {ComponentType, Suspense} from 'react'
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {createBrowserRouter, createRoutesFromElements, Route, RouterProvider} from "react-router-dom";
import {PortalAppMenu} from "./PortalAppMenu";

interface AppProps {
    Sidebar : ComponentType<{
        className?: string;
    }>
    children? : React.ReactNode;
}

/**
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());

export const PortalApp = ({Sidebar, children} : AppProps) =>
{
    /**
     * i18next translation hook
     */
    const {t} = useTranslation(['translation', 'override']);

    const validateOTPFunc = async (totp : string, options?: RawAxiosRequestConfig) => {
        return await authApi.authControllerValidateTOTP(totp, undefined, options);
    };

    const router = createBrowserRouter(
        createRoutesFromElements(
            <>
                {/* some pages should be accessible without authentication */}
                <Route path="/" errorElement={<ErrorPage/>} element={
                    <LoginLayout/>
                }>
                    <Route path="login" element={
                        <PortalLogin/>
                    } />
                    <Route path="forgot-password" element={
                        <PortalForgotPassword/>
                    } />
                    <Route path="password-reset" element={
                        <PortalPasswordReset/>
                    } />
                    <Route path="signup" element={
                        <PortalSignup/>
                    } />
                </Route>
                <Route path={"tfa"} element={
                    <Tfa validateOTPFunc={validateOTPFunc}/>
                } />
                <Route path={"success"} element={
                    <Success/>
                } />
                <Route path="pdf/reply" element={
                    <PDFReply/>
                } />

                <Route path="/" element={
                    // all child "pages" (except login) will be wrapped by the following context providers
                    // and will have the AdminLayout as the parent component
                    // because the AdminLayout is wrapped with an AuthenticatedRoute, all access to the admin
                    // pages requires authentication
                    <SpinnerStatusContextProvider>
                        <AuthenticatedUserContextProvider>
                            <AuthenticatedRoute><AppLayout AppSidebar={Sidebar} Menu={PortalAppMenu}/></AuthenticatedRoute>
                        </AuthenticatedUserContextProvider>
                    </SpinnerStatusContextProvider>
                }>
                    {/* The below child routes are wrapped by the AdminLayout and require authentication */}
                    <Route index element={
                        <PortalHome/>
                    } />
                    <Route path={"pdf/otp"} element={
                        <PDFGeneratePassword/>
                    } />
                    {children}
                </Route>
            </>
        )
    );

    return (
        <React.StrictMode>
            {createPortal(
                <>
                    <link rel="icon" href={t(["override:favicon", "favicon"])} />
                    <title>{t(["override:portal.title", "portal.title"])}</title>
                </>,
                document.head
            )}

            <Suspense fallback={<Loading/>}>
                <RouterProvider router={router} />
            </Suspense>
        </React.StrictMode>
    )
}
